<template>
  <Teleport to="body">
    <div v-if="isOpen" :class="['relative z-50', className]">
      <!-- Backdrop -->
      <div
        class="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
        aria-hidden="true"
        @click="onBackdropClick"
      />

      <!-- Modal -->
      <div
        class="fixed inset-0 overflow-y-auto"
        @click="onBackdropClick"
      >
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <div
            ref="dialogRef"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="title ? titleId : undefined"
            :aria-label="!title ? ariaLabel : undefined"
            tabindex="-1"
            class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:bg-gray-800 dark:focus-visible:ring-offset-gray-900"
            :class="{ 'max-w-lg': size === 'lg', 'max-w-xl': size === 'xl' }"
            @click.stop
            @keydown="onKeydown"
          >
            <h3
              v-if="title"
              :id="titleId"
              class="mb-4 text-lg font-medium leading-6 text-gray-900 dark:text-gray-100"
            >
              {{ title }}
            </h3>

            <slot />

            <div v-if="$slots.footer" class="mt-4">
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, useId, watch, nextTick, onUnmounted } from 'vue'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    title?: string
    /** Used when no visible title is provided */
    ariaLabel?: string
    size?: 'md' | 'lg' | 'xl'
    className?: string
  }>(),
  {
    ariaLabel: 'Dialog',
    size: 'md',
  },
)

const emit = defineEmits<{
  (e: 'close'): void
}>()

const dialogRef = ref<HTMLElement | null>(null)
const titleId = useId()
let previouslyFocused: HTMLElement | null = null

const close = () => {
  emit('close')
}

// Ignore backdrop clicks for a short window after open so the opening click
// cannot immediately dismiss a freshly teleported modal.
let ignoreBackdropClicksUntil = 0

const onBackdropClick = () => {
  if (Date.now() < ignoreBackdropClicksUntil) return
  close()
}

const getFocusableElements = (): HTMLElement[] => {
  if (!dialogRef.value) return []
  return Array.from(dialogRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true',
  )
}

const focusInitial = async () => {
  await nextTick()
  const focusable = getFocusableElements()
  if (focusable.length > 0) {
    focusable[0].focus()
  } else {
    dialogRef.value?.focus()
  }
}

const trapFocus = (e: KeyboardEvent) => {
  if (e.key !== 'Tab' || !dialogRef.value) return

  const focusable = getFocusableElements()
  if (focusable.length === 0) {
    e.preventDefault()
    dialogRef.value.focus()
    return
  }

  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement as HTMLElement | null

  if (e.shiftKey) {
    if (active === first || active === dialogRef.value) {
      e.preventDefault()
      last.focus()
    }
  } else if (active === last) {
    e.preventDefault()
    first.focus()
  }
}

const onKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    e.stopPropagation()
    close()
    return
  }
  trapFocus(e)
}

const restoreFocus = () => {
  previouslyFocused?.focus?.()
  previouslyFocused = null
}

watch(
  () => props.isOpen,
  async (open) => {
    if (open) {
      previouslyFocused = document.activeElement as HTMLElement | null
      ignoreBackdropClicksUntil = Date.now() + 300
      await focusInitial()
    } else {
      restoreFocus()
    }
  },
)

onUnmounted(() => {
  if (props.isOpen) restoreFocus()
})
</script>
